import { isApiError } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Icon, Input, Select, Switch } from "@sagot/ui";
import { useId, useState, type SyntheticEvent } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import {
  GLOSSARY_LIMITS,
  GLOSSARY_LOCALES,
  draftOf,
  emptyDraft,
  newSynonym,
  termProblem,
  toCreate,
  toPatch,
  type GlossaryLocale,
  type GlossaryScope,
  type GlossaryTerm,
  type TermDraft,
} from "./glossary-model";
import { useGlossaryMutations } from "./use-glossary";

const CONFLICT = 409;

interface DialogProps {
  agentId: string;
  /** The term being edited; `null` adds a new one. */
  editing: GlossaryTerm | null;
  onClose: (saved: "added" | "saved" | null) => void;
}

function LocaleSelect({ id, label, value, onChange }: { id?: string; label: string; value: GlossaryLocale; onChange: (next: GlossaryLocale) => void }) {
  const { t } = useTranslation();
  return (
    <Select id={id} aria-label={id ? undefined : label} value={value} onChange={(event) => { onChange(event.target.value as GlossaryLocale); }}>
      {GLOSSARY_LOCALES.map((locale) => <option key={locale} value={locale}>{t(`glossary.form.localeOptions.${locale}`)}</option>)}
    </Select>
  );
}

function SynonymRows({ draft, onChange }: { draft: TermDraft; onChange: (next: TermDraft) => void }) {
  const { t } = useTranslation();
  const full = draft.synonyms.length >= GLOSSARY_LIMITS.synonymsMax;
  const patch = (key: number, change: Partial<{ text: string; locale: GlossaryLocale }>) => {
    onChange({ ...draft, synonyms: draft.synonyms.map((s) => (s.key === key ? { ...s, ...change } : s)) });
  };
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-semibold">{t("glossary.form.synonyms")}</legend>
      <p className="text-xs text-fg-subtle">{t("glossary.form.synonymsHint", { max: GLOSSARY_LIMITS.synonymsMax })}</p>
      {draft.synonyms.map((synonym, index) => (
        <div key={synonym.key} className="grid grid-cols-[1fr_auto] items-center gap-2 sm:grid-cols-[1fr_9rem_auto]">
          <Input
            aria-label={t("glossary.form.synonymText", { n: index + 1 })}
            maxLength={GLOSSARY_LIMITS.termMax}
            value={synonym.text}
            onChange={(event) => { patch(synonym.key, { text: event.target.value }); }}
          />
          <div className="order-last col-span-2 sm:order-none sm:col-span-1">
            <LocaleSelect label={t("glossary.form.synonymLocale", { n: index + 1 })} value={synonym.locale} onChange={(locale) => { patch(synonym.key, { locale }); }} />
          </div>
          <Button type="button" variant="ghost" iconOnly aria-label={t("glossary.form.removeSynonym", { n: index + 1 })} onClick={() => { onChange({ ...draft, synonyms: draft.synonyms.filter((s) => s.key !== synonym.key) }); }}>
            <Icon name="x" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="secondary" size="sm" disabled={full} onClick={() => { onChange({ ...draft, synonyms: [...draft.synonyms, newSynonym()] }); }}>
        <Icon name="plus" />
        {t("glossary.form.addSynonym")}
      </Button>
    </fieldset>
  );
}

function FlagRow({ label, hint, checked, onChange }: { label: string; hint: string; checked: boolean; onChange: (next: boolean) => void }) {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <label id={`${id}-name`} htmlFor={id} className="text-sm font-semibold">{label}</label>
        <p id={`${id}-hint`} className="mt-0.5 text-xs text-fg-subtle">{hint}</p>
      </div>
      <Switch id={id} aria-labelledby={`${id}-name`} aria-describedby={`${id}-hint`} className="mt-0.5 shrink-0" checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function ScopeField({ draft, onChange }: { draft: TermDraft; onChange: (next: TermDraft) => void }) {
  const { t } = useTranslation();
  return (
    <Field label={t("glossary.form.scope")}>
      <Select value={draft.scope} onChange={(event) => { onChange({ ...draft, scope: event.target.value as GlossaryScope }); }}>
        <option value="all">{t("glossary.tab.scopeAll")}</option>
        <option value="agent">{t("glossary.tab.scopeThis")}</option>
      </Select>
    </Field>
  );
}

/** Add or edit one term. Closes itself through `onClose`; the list refreshes from the mutations. */
export function GlossaryTermDialog({ agentId, editing, onClose }: DialogProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { create, update } = useGlossaryMutations(agentId);
  const [draft, setDraft] = useState<TermDraft>(() => (editing ? draftOf(editing) : emptyDraft()));
  const [touched, setTouched] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const pending = create.isPending || update.isPending;
  const problem = termProblem(draft);
  const termError = touched && problem ? t(problem === "required" ? "glossary.form.termRequired" : "glossary.form.termTooLong", { max: GLOSSARY_LIMITS.termMax }) : null;

  const fail = (error: unknown) => {
    const conflict = isApiError(error) && error.status === CONFLICT;
    if (!conflict) {
      setFailure(message(error));
      return;
    }
    setFailure(t(error.code === "plan_limit_reached" ? "glossary.form.full" : "glossary.form.duplicate"));
  };
  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation(); // the dialog sits inside the agent settings form in the React tree
    setTouched(true);
    if (problem) return;
    setFailure(null);
    if (editing) update.mutate({ id: editing.id, body: toPatch(draft) }, { onSuccess: () => { onClose("saved"); }, onError: fail });
    else create.mutate(toCreate(draft, agentId), { onSuccess: () => { onClose("added"); }, onError: fail });
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(null); }}>
      <DialogContent
        title={t(editing ? "glossary.form.editTitle" : "glossary.form.addTitle")}
        description={t("glossary.form.description")}
        closeLabel={t("common.close")}
        className="w-full max-w-lg"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => { onClose(null); }}>{t("glossary.tab.cancel")}</Button>
            <Button type="submit" form="glossary-term-form" loading={pending}>{t("glossary.form.save")}</Button>
          </>
        }
      >
        <form id="glossary-term-form" noValidate className="space-y-5" onSubmit={submit}>
          <Field label={t("glossary.form.term")} hint={t("glossary.form.termHint")} error={termError}>
            <Input value={draft.term} maxLength={GLOSSARY_LIMITS.termMax + 20} invalid={Boolean(termError)} onChange={(event) => { setDraft({ ...draft, term: event.target.value }); }} onBlur={() => { setTouched(true); }} />
          </Field>
          <Field label={t("glossary.form.locale")}>
            <LocaleSelect value={draft.locale} label={t("glossary.form.locale")} onChange={(locale) => { setDraft({ ...draft, locale }); }} />
          </Field>
          <SynonymRows draft={draft} onChange={setDraft} />
          <div className="space-y-4 rounded-md bg-surface-2 p-4">
            <FlagRow label={t("glossary.flags.protected")} hint={t("glossary.flags.protectedHint")} checked={draft.protected} onChange={(next) => { setDraft({ ...draft, protected: next }); }} />
            <FlagRow label={t("glossary.flags.escalation")} hint={t("glossary.flags.escalationHint")} checked={draft.escalation} onChange={(next) => { setDraft({ ...draft, escalation: next }); }} />
          </div>
          {editing ? null : <ScopeField draft={draft} onChange={setDraft} />}
          {failure ? <p role="alert" className="text-sm font-semibold text-danger">{failure}</p> : null}
        </form>
      </DialogContent>
    </Dialog>
  );
}
